// Keep a saree and its Avis stitching lines in step. Inventory validation stays
// on change.js for the saree; only service quantities use update.js.
const muralikaStitchingCart = {
  serviceProduct: '8857027215574',
  busy: false,
  editPending: false,
  blocked: false,
  initialized: true,
  revision: 0,
  checking: null,
  lastMessage: '',
  error: 'We could not confirm your saree and stitching quantities. Please refresh your cart before checking out.',
  order(item) {
    const value = String(item.properties?._apo_order ?? '');
    const service = String(item.product_id) === this.serviceProduct;
    const match = value.match(service ? /^Added by:\s*(\d+)\s*\*\s*1$/ : /^(\d+)$/);
    return match?.[1] || null;
  },
  groups(cart) {
    const groups = new Map();
    for (const item of cart.items || []) {
      const order = this.order(item);
      if (!order) continue;
      if (!groups.has(order)) groups.set(order, { order, parents: [], services: [] });
      groups.get(order)[String(item.product_id) === this.serviceProduct ? 'services' : 'parents'].push(item);
    }
    return groups;
  },
  groupIssue(group, checkQuantity = true) {
    const property = group.parents[0]?.properties?.['Do you want to stitch'] || '';
    if (!group.services.length && !property) return '';
    if (group.parents.length !== 1) return 'A stitching service is no longer linked to one saree. Please remove it and add your saree with its service again.';
    const expected = [
      ['Fall and Edging', '46512641048790'],
      ['Blouse Stitching', '47203797532886'],
      ['Ready to wear', '46512641016022'],
    ].filter(([label]) => property.includes(label)).map(([, id]) => id);
    const ids = group.services.map(item => String(item.variant_id || item.id));
    if (expected.some(id => !ids.includes(id)) || (expected.length && ids.some(id => !expected.includes(id))) || new Set(ids).size !== ids.length) return this.error;
    if (ids.includes('46512641048790') && ids.includes('46512641016022')) return 'Ready to wear already includes fall and edging. Please remove this saree and add it again with one of those services.';
    if (checkQuantity && group.services.some(item => item.quantity !== group.parents[0].quantity)) return 'Your saree and stitching quantities do not match yet. Update the saree quantity again, or remove it and add it again with stitching.';
    return '';
  },
  issue(cart) {
    if (!Array.isArray(cart?.items)) return this.error;
    if (cart.items.some(item => (String(item.product_id) === this.serviceProduct ||
        item.properties?.['Do you want to stitch']) && !this.order(item))) return 'A stitching service is no longer linked to one saree. Please remove it and add your saree with its service again.';
    for (const group of this.groups(cart).values()) {
      const issue = this.groupIssue(group);
      if (issue) return issue;
    }
    return '';
  },
  propertyFingerprint(properties) {
    return JSON.stringify(Object.entries(properties || {}).sort(([left], [right]) => left.localeCompare(right)));
  },
  findBoundParent(cart, binding) {
    if (!binding || !Array.isArray(cart?.items)) return null;
    const identity = item => String(item.product_id) !== this.serviceProduct &&
      String(item.product_id) === String(binding.productId) &&
      String(item.variant_id || item.id) === String(binding.variantId);
    const matches = cart.items.filter(identity);
    const keyed = matches.find(item => item.key === binding.key);
    if (keyed && (!binding.order || this.order(keyed) === String(binding.order) ||
        (!this.order(keyed) && binding._plainProperties &&
          this.propertyFingerprint(keyed.properties) === this.propertyFingerprint(binding._plainProperties)))) return keyed;
    if (binding.order) {
      const group = this.groups(cart).get(String(binding.order));
      if (group?.parents.length > 1) throw new Error('Your cart has more than one matching saree. Please review it before changing stitching.');
      if (group?.parents.length === 1) {
        if (!identity(group.parents[0])) throw new Error(this.error);
        return group.parents[0];
      }
    }
    // Discounts can change a plain item's key. Match its original properties
    // only while it has no association; never guess among same-variant groups.
    const fingerprint = this.propertyFingerprint(binding._plainProperties || binding.properties);
    const plain = matches.filter(item => !this.order(item) &&
      this.propertyFingerprint(item.properties) === fingerprint);
    if ((!binding.order || binding._plainProperties) && plain.length > 1) throw new Error('Your cart has more than one matching saree. Please review it before changing stitching.');
    return (!binding.order || binding._plainProperties) && plain.length === 1 ? plain[0] : null;
  },
  rememberParent(binding, parent) {
    Object.assign(binding, {
      key: parent.key,
      productId: String(parent.product_id),
      variantId: String(parent.variant_id || parent.id),
      order: this.order(parent),
      properties: { ...(parent.properties || {}) },
    });
    if (binding.order) delete binding._plainProperties;
    return binding;
  },
  validSelection(selection) {
    const labels = { '46512641048790': 'Fall and Edging', '47203797532886': 'Blouse Stitching', '46512641016022': 'Ready to wear' };
    return Array.isArray(selection) && selection.every(item => labels[String(item.id)] &&
      typeof item.label === 'string' && item.label.trim().startsWith(labels[String(item.id)])) &&
      new Set(selection.map(item => String(item.id))).size === selection.length &&
      !(selection.some(item => String(item.id) === '46512641048790') && selection.some(item => String(item.id) === '46512641016022'));
  },
  selectionMatches(cart, binding, selection) {
    if (!this.validSelection(selection)) return false;
    try {
      const parent = this.findBoundParent(cart, binding);
      if (!parent) return false;
      const property = parent.properties?.['Do you want to stitch'] || '';
      if (property !== selection.map(item => item.label).join(', ')) return false;
      if (selection.length && parent.properties?.['Estimated Arrival date.']) return false;
      const group = this.groups(cart).get(this.order(parent));
      const services = group?.services || [];
      return services.length === selection.length && selection.every(selected =>
        services.some(item => String(item.variant_id || item.id) === String(selected.id) && item.quantity === parent.quantity)) &&
        !this.issue(cart);
    } catch { return false; }
  },
  async editServices(binding, selection) {
    // The PDP controller owns the lock across coalesced choices, rendering and
    // reconciliation. This method never retries a potentially committed write.
    if (!this.busy) throw new Error('Please wait while your stitching selection updates.');
    if (!this.validSelection(selection)) throw new Error('Please select either Ready to wear or Fall and Edging.');
    let cart = await this.read();
    let parent = this.findBoundParent(cart, binding);
    if (!parent) return { cart, binding, removed: true };
    let order = this.order(parent);
    if (parent.properties?._apo_order && !order) throw new Error(this.error);
    const validateGroup = () => {
      const group = order && this.groups(cart).get(order);
      if (group && (group.parents.length !== 1 || group.parents[0].key !== parent.key)) throw new Error(this.error);
      const services = group?.services || [];
      const ids = services.map(item => String(item.variant_id || item.id));
      if (new Set(ids).size !== ids.length || ids.some(id => !['46512641048790', '47203797532886', '46512641016022'].includes(id))) throw new Error(this.error);
      return services;
    };
    validateGroup();
    if (this.selectionMatches(cart, binding, selection)) return { cart, binding: this.rememberParent(binding, parent), removed: false };
    if (!order && selection.length) {
      let candidate = Date.now();
      const orders = this.groups(cart);
      while (orders.has(String(candidate))) candidate++;
      order = String(candidate);
      // Preserve this identity before the write: its response can be lost even
      // when Shopify successfully changes the parent's key and properties.
      binding._plainProperties = { ...(parent.properties || {}) };
      binding.order = order;
    }
    const properties = { ...(parent.properties || {}) };
    if (order) properties._apo_order = order;
    if (selection.length) {
      properties['Do you want to stitch'] = selection.map(item => item.label).join(', ');
      delete properties['Estimated Arrival date.'];
    } else delete properties['Do you want to stitch'];
    if (this.propertyFingerprint(properties) !== this.propertyFingerprint(parent.properties)) {
      cart = await this.request(routes.cart_change_url, { id: parent.key, quantity: parent.quantity, properties });
      parent = this.findBoundParent(cart, binding);
      if (!parent) throw new Error(this.error);
      this.rememberParent(binding, parent);
    }
    let services = validateGroup();
    const missing = selection.filter(selected => !services.some(item => String(item.variant_id || item.id) === String(selected.id)));
    if (missing.length) {
      await this.request(routes.cart_add_url || `${routes.cart_url}/add.js`, {
        items: missing.map(selected => ({
          id: Number(selected.id), quantity: parent.quantity,
          properties: { _apo_order: `Added by: ${order} * 1`, _apo_option: `Do you want to stitch: ${selected.label}` },
        })),
      });
    }
    // Adding/changing a service can alter discount-dependent line keys. A
    // fresh cart is required before removing obsolete fees or matching qty.
    cart = await this.read();
    parent = this.findBoundParent(cart, binding);
    if (!parent) throw new Error(this.error);
    services = validateGroup();
    if (selection.some(selected => !services.some(item => String(item.variant_id || item.id) === String(selected.id)))) throw new Error(this.error);
    const updates = {};
    for (const service of services) {
      const wanted = selection.some(selected => String(selected.id) === String(service.variant_id || service.id));
      const quantity = wanted ? parent.quantity : 0;
      if (service.quantity !== quantity) updates[service.key] = quantity;
    }
    if (Object.keys(updates).length) cart = await this.request(routes.cart_update_url, { updates });
    if (!this.selectionMatches(cart, binding, selection)) throw new Error(this.error);
    parent = this.findBoundParent(cart, binding);
    return { cart, binding: this.rememberParent(binding, parent), removed: false };
  },
  async request(url, body) {
    if (body && /\/cart\/(add|change|update)$/.test(url)) url += '.js';
    const response = await fetch(url, body ? { ...fetchConfig(), body: JSON.stringify(body) } : { cache: 'no-store' });
    let state;
    try { state = await response.json(); } catch { throw new Error(this.error); }
    if (!response.ok || state.errors || state.status >= 400) {
      throw new Error(typeof state.errors === 'string' ? state.errors : state.description || this.error);
    }
    return state;
  },
  read() {
    return this.request(`${routes.cart_url}.js`);
  },
  syncLock() {
    const locked = this.busy || this.editPending || this.blocked || !!this.checking || !this.initialized;
    document.documentElement.toggleAttribute('data-muralika-cart-locked', locked);
    document.querySelectorAll('[name="checkout"], .cart__checkout-button').forEach(button => {
      if (locked && !button.disabled) {
        button.dataset.muralikaCartDisabled = 'true';
        button.disabled = true;
      } else if (!locked && button.dataset.muralikaCartDisabled === 'true') {
        delete button.dataset.muralikaCartDisabled;
        button.disabled = false;
      }
    });
  },
  showError(message) {
    document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach(element => { element.textContent = message; });
    this.lastMessage = message;
  },
  clearError() {
    document.querySelectorAll('#cart-errors, #CartDrawer-CartErrors').forEach(element => {
      if (element.textContent === this.lastMessage) element.textContent = '';
    });
    this.lastMessage = '';
  },
  refresh(cartData) {
    if (this.busy || this.checking) return this.checking;
    if (Array.isArray(cartData?.items) && typeof cartData.item_count === 'number') {
      const issue = this.issue(cartData);
      this.blocked = !!issue;
      this.initialized = true;
      if (issue) this.showError(issue); else this.clearError();
      this.syncLock();
      return Promise.resolve();
    }
    const responseHasStitching = [cartData, ...(cartData?.items || [])].some(item =>
      item && (String(item.product_id) === this.serviceProduct || item.properties?.['Do you want to stitch']));
    if (!responseHasStitching && !document.querySelector('cart-items[data-muralika-stitching-cart="true"], cart-drawer-items[data-muralika-stitching-cart="true"]')) {
      this.blocked = false;
      this.initialized = true;
      this.clearError();
      this.syncLock();
      return Promise.resolve();
    }
    const revision = this.revision;
    this.checking = this.read().then(cart => {
      if (revision !== this.revision || this.busy) return;
      const issue = this.issue(cart);
      this.blocked = !!issue;
      this.initialized = true;
      if (issue) this.showError(issue); else this.clearError();
    }).catch(() => {
      if (revision !== this.revision || this.busy) return;
      this.blocked = true;
      this.showError(this.error);
    }).finally(() => { this.checking = null; this.syncLock(); });
    this.syncLock();
    return this.checking;
  },
};

window.addEventListener('click', event => {
  if ((muralikaStitchingCart.busy || muralikaStitchingCart.editPending) && event.target?.closest?.('.product-form__submit, .avis-new-addcart-button, .avis-checkout')) {
    event.preventDefault();
    event.stopImmediatePropagation();
    muralikaStitchingCart.showError('Updating your saree and stitching. Please wait.');
    return;
  }
  const target = event.target?.closest?.('[name="checkout"], .cart__checkout-button, a[href*="/checkout"], .cart__dynamic-checkout-buttons, .additional-checkout-buttons, .gokwik-checkout, .gokwik-marge');
  if (!target || !(muralikaStitchingCart.busy || muralikaStitchingCart.editPending || muralikaStitchingCart.blocked || muralikaStitchingCart.checking || !muralikaStitchingCart.initialized)) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  muralikaStitchingCart.showError((muralikaStitchingCart.busy || muralikaStitchingCart.editPending) ? 'Updating your saree and stitching. Please wait.' : muralikaStitchingCart.error);
}, true);
window.addEventListener('submit', event => {
  if (!(muralikaStitchingCart.busy || muralikaStitchingCart.editPending || muralikaStitchingCart.blocked || muralikaStitchingCart.checking || !muralikaStitchingCart.initialized)) return;
  const cartForm = event.target?.matches?.('form[action$="/cart"], form[action$="/checkout"]');
  const addingDuringUpdate = (muralikaStitchingCart.busy || muralikaStitchingCart.editPending) && event.target?.matches?.('form[action$="/cart/add"]');
  if (!cartForm && !addingDuringUpdate) return;
  event.preventDefault();
  event.stopImmediatePropagation();
  muralikaStitchingCart.showError((muralikaStitchingCart.busy || muralikaStitchingCart.editPending) ? 'Updating your saree and stitching. Please wait.' : muralikaStitchingCart.error);
}, true);

class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0, undefined, undefined, this.closest('[data-cart-item-key]')?.dataset.cartItemKey);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    muralikaStitchingCart.refresh();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
      muralikaStitchingCart.refresh(event.cartData);
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;
    let message = '';

    if (inputValue < event.target.dataset.min) {
      message = window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min);
    } else if (inputValue > parseInt(event.target.max)) {
      message = window.quickOrderListStrings.max_error.replace('[max]', event.target.max);
    } else if (inputValue % parseInt(event.target.step) !== 0) {
      message = window.quickOrderListStrings.step_error.replace('[step]', event.target.step);
    }

    if (message) {
      this.setValidity(event, index, message);
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.updateQuantity(
        index,
        inputValue,
        document.activeElement.getAttribute('name'),
        event.target.dataset.quantityVariantId,
        event.target.closest('[data-cart-item-key]')?.dataset.cartItemKey
      );
    }
  }

  onChange(event) {
    this.validateQuantity(event);
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
      {
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section',
      },
      {
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      },
    ];
  }

  async updateQuantity(line, quantity, name, variantId, itemKey) {
    const guard = muralikaStitchingCart;
    if (guard.busy) {
      guard.showError('Updating your saree and stitching. Please wait.');
      return;
    }
    const row = this.querySelector(`#CartItem-${line}`) || this.querySelector(`#CartDrawer-Item-${line}`);
    const key = itemKey || row?.dataset.cartItemKey;
    let protectsStitching = guard.blocked || !!document.querySelector('cart-items[data-muralika-stitching-cart="true"], cart-drawer-items[data-muralika-stitching-cart="true"]');
    guard.busy = true;
    guard.revision++;
    guard.syncLock();
    let sections;
    try {
      this.enableLoading(line);
      sections = {
        sections: this.getSectionsToRender().map(section => section.section),
        sections_url: window.location.pathname,
      };
      if (!key || !Number.isInteger(Number(quantity)) || Number(quantity) < 0) throw new Error('Your cart changed. Please refresh it and try again.');
      quantity = Number(quantity);
      const before = await guard.read();
      protectsStitching ||= before.items.some(item => String(item.product_id) === guard.serviceProduct || item.properties?.['Do you want to stitch']);
      const item = before.items.find(item => item.key === key);
      if (!item) throw new Error('Your cart changed. Please review its current items and try again.');
      const order = guard.order(item);
      const group = order && guard.groups(before).get(order);
      const isService = String(item.product_id) === guard.serviceProduct;
      const linked = group && (group.services.length || group.parents[0]?.properties?.['Do you want to stitch']);
      let state;
      if (linked && !isService) {
        if (group.parents.length !== 1) throw new Error(guard.groupIssue(group));
        if (quantity === 0) {
          // Removing a saree and all its linked services can be one atomic write.
          const updates = Object.fromEntries([item, ...group.services].map(item => [item.key, 0]));
          state = await guard.request(routes.cart_update_url, { updates, ...sections });
        } else {
          const issue = guard.groupIssue(group, false);
          if (issue) throw new Error(issue);
          // change.js enforces the saree's available inventory. Never apply a
          // positive parent quantity using update.js, which skips that check.
          state = await guard.request(routes.cart_change_url, { id: key, quantity, ...sections });
          const changedGroup = guard.groups(state).get(order);
          if (!changedGroup || changedGroup.parents.length !== 1 ||
              changedGroup.parents[0].product_id !== item.product_id ||
              String(changedGroup.parents[0].variant_id || changedGroup.parents[0].id) !== String(item.variant_id || item.id)) throw new Error(guard.error);
          const changedIssue = guard.groupIssue(changedGroup, false);
          if (changedIssue) throw new Error(changedIssue);
          const actualQuantity = changedGroup.parents[0].quantity;
          const updates = Object.fromEntries(changedGroup.services
            .filter(service => service.quantity !== actualQuantity)
            .map(service => [service.key, actualQuantity]));
          if (Object.keys(updates).length) state = await guard.request(routes.cart_update_url, { updates, ...sections });
        }
      } else {
        // A linked service is edited with its parent. An orphan can be removed.
        if (isService && (quantity !== 0 || group?.parents.length)) throw new Error('Edit or remove this stitching service with its saree.');
        state = await guard.request(routes.cart_change_url, { id: key, quantity, ...sections });
      }
      const issue = guard.issue(state);
      guard.blocked = !!issue;
      guard.initialized = true;
      this.renderCartState(state, line, name, variantId);
      const actual = state.items.find(item => item.key === key) ||
        (order && guard.groups(state).get(order)?.parents[0]);
      const quantityMessage = quantity > 0 && actual && actual.quantity !== quantity
        ? window.cartStrings.quantityError.replace('[quantity]', actual.quantity) : '';
      this.updateLiveRegions(line, issue || quantityMessage);
      guard.showError(issue || quantityMessage);
    } catch (error) {
      // A network failure may arrive after a successful server write. Read and
      // render the real cart, but never repeat an uncertain mutation.
      guard.blocked = protectsStitching;
      let message = error.message || guard.error;
      try {
        const current = await guard.read();
        const issue = guard.issue(current);
        guard.blocked = !!issue;
        guard.initialized = true;
        const sectionState = await guard.request(`${routes.cart_url}?sections=${encodeURIComponent(sections.sections.join(','))}`);
        this.renderCartState({ ...current, sections: sectionState }, line, name, variantId);
        if (issue) message = issue;
        else {
          const confirmed = current.items.find(item => item.key === key);
          if ((quantity === 0 && !confirmed) || (confirmed && confirmed.quantity === quantity)) message = '';
        }
      } catch (reconcileError) {
        guard.blocked = protectsStitching;
        message = protectsStitching ? guard.error : window.cartStrings.error;
      }
      guard.showError(message);
      this.updateLiveRegions(line, message);
    } finally {
      try { this.disableLoading(line); } finally {
        guard.busy = false;
        guard.syncLock();
      }
    }
  }

  renderCartState(parsedState, line, name, variantId) {
        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

        this.getSectionsToRender().forEach((section) => {
          if (!document.getElementById(section.id)) return;
          if (!parsedState.sections?.[section.section]) throw new Error(muralikaStitchingCart.error);
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').textContent = message;

    this.lineItemStatusElement?.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus?.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus?.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
